/*
    Allow message to be lazy formatted, so that the code will not spend time
    formatting the message unless the invariant actually got broken, and we
    therefore need a message to report.
*/
type MessageValue = string;
type MessageFormatter = MessageValue | (() => MessageValue);

function toString(msg: MessageValue) {
  if (typeof msg === 'string') {
    return msg;
  }
  return JSON.stringify(msg);
}

/**
 * This function prints a console message and throws an error if the given condition is
 * false.
 *
 * It is meant to be a safety net to prevent programming mistakes.
 * It MUST NOT BE USED for regular control flow !
 *
 * For instance, if some code is expected to throw errors, then it should not use
 * "invariant(condition, msg);" but instead use "if (!condition) throw new Error(msg);"
 *
 * Also, be aware that invoking "invariant(condition, string)" can incur a performance cost
 * if the string is a templated one, since it will be evaluated whether or not the condition
 * is true. Therefore, when using invariant() in performance critical code, prefer using a
 * function like this, which will create the string only when the condition is false:
 *
 * invariant(abc !== expected, () => `Object ${id} with props ${JSON.stringify(props))} has an abc value of ${abc} instead of ${expected}`);
 */
export function invariant(value: any, message: MessageFormatter): asserts value {
  if (!value) {
    const msg = toString(typeof message === 'function' ? message() : message);
    console.assert(value, toString(msg));
    throw new Error(msg);
  }
}

// Minimal declaration for TypeScript compilation in non-DOM contexts
declare const mimirEnv: Record<string, string | undefined> | undefined;

function isNodeEnvDevelopmentOrUnitTest() {
  if (typeof mimirEnv !== 'undefined') {
    // Browser environment
    return mimirEnv.NODE_ENV === 'development';
  }

  if (typeof process !== 'undefined') {
    // Node.js environment
    const stage = process.env['MIMIR_STAGE'];
    return process.env.NODE_ENV === 'development' || stage == 'ut' || stage == null;
  }

  return false;
}

/**
 * @deprecated This function is only failing in development mode and in unit tests,
 *             but not in production. It is meant for very specific cases where
 *             we want to check a condition without risking to break production.
 *
 *             Using this function implies that the developer will follow up on
 *             production logs after deploy to see if the condition fails or not.
 *             Based on that, the code should be revisited to properly handle the
 *             condition instead of using assert().
 *
 *             When writing new code, developers should always use invariant instead().
 */
export function assert(condition: any, message: MessageFormatter): asserts condition {
  /*
        Warning: WebPack magic

        Webpack will make process.env.NODE_ENV code work in the browser.
    */
  const value = typeof condition === 'function' ? condition() : condition;
  if (value) {
    return;
  }
  if (isNodeEnvDevelopmentOrUnitTest()) {
    invariant(value, message);
  } else {
    // If not in dev mode, let's still log a warning
    const msg = toString(typeof message === 'function' ? message() : message);
    console.warn('assert failure:', toString(msg));
  }
}

/*
    Evaluator will only be run and checked when in development mode.
*/
export async function asyncAssert(evaluator: () => Promise<any>, message: MessageFormatter): Promise<void> {
  /*
        Warning: WebPack magic

        Webpack will make process.env.NODE_ENV code work in the browser.
    */
  if (isNodeEnvDevelopmentOrUnitTest()) {
    const value = await evaluator();
    invariant(value, message);
  }
}
